/*
 * Copyright (c) 2020. American Express Company
 */

import BWDataService from './bw-data.service';
import { IncomeSourceRuleModel } from 'src/types/income-source-rule.model';
import { TransactionModel } from 'src/types/transaction.model';
import { map } from 'lodash';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import { TransactionType } from 'src/types/transaction-type.enum';

export default class IncomeSourceRuleService extends BWDataService {
    public static async createOrUpdateRule(rule: Partial<IncomeSourceRuleModel>): Promise<void> {
        if (rule.incomeRuleUid) {
            await this.updateRule(rule);
        } else {
            await this.postRule(rule);
        }
    }

    public static async createRule(ruleToBeCreated: Partial<IncomeSourceRuleModel>): Promise<IncomeSourceRuleModel> {
        return await this.postRule(ruleToBeCreated);
    }

    public static async createRules(rules: Array<Partial<IncomeSourceRuleModel>>): Promise<IncomeSourceRuleModel> {
        return await this.post(
            '/incomerules/batch-create',
            map(rules, (r: Partial<IncomeSourceRuleModel>) => ({
                priority: r.priority,
                incomeUid: r.incomeUid,
                transactionNamePattern: r.transactionNamePattern,
                matcher: r.matcher
            }))
        );
    }

    private static async postRule(rule: Partial<IncomeSourceRuleModel>): Promise<IncomeSourceRuleModel> {
        return await this.post('/incomerules', {
            priority: rule.priority,
            incomeUid: rule.incomeUid,
            matcher: rule.matcher,
            transactionNamePattern: rule.transactionNamePattern
        });
    }

    public static async updateRule(rule: Partial<IncomeSourceRuleModel>): Promise<IncomeSourceRuleModel> {
        return await this.put(`/incomerules/${rule.incomeRuleUid}`, {
            priority: rule.priority,
            incomeUid: rule.incomeUid,
            matcher: rule.matcher,
            transactionNamePattern: rule.transactionNamePattern
        });
    }

    public static async getRules(): Promise<IncomeSourceRuleModel[]> {
        return await this.get('/incomerules');
    }

    public static async deleteRule(rule: Partial<IncomeSourceRuleModel>): Promise<number> {
        return await this.delete('/incomerules/' + rule.incomeRuleUid);
    }

    public static matchesRule(transaction: TransactionModel, rule: IncomeSourceRuleModel): Boolean {
        if (!rule.matcher || transaction.type !== TransactionType.TRANSACTION_INCOME) {
            return false;
        }

        switch (rule.matcher) {
            case RuleMatcher.ENDS_WITH:
                return transaction.displayName.toLowerCase().endsWith(rule.transactionNamePattern.toLowerCase());

            case RuleMatcher.CONTAINS:
                return transaction.displayName.toLowerCase().includes(rule.transactionNamePattern.toLowerCase());

            case RuleMatcher.STARTS_WITH:
                return transaction.displayName.toLowerCase().startsWith(rule.transactionNamePattern.toLowerCase());

            case RuleMatcher.EQUALS:
                return transaction.displayName.toLowerCase() === rule.transactionNamePattern.toLowerCase();
        }
    }

    /** Retrieves the priorities from a list of income rules */
    public static getPriorities(incomeSourceRules: IncomeSourceRuleModel[]): number[] {
        return map(incomeSourceRules, 'priority');
    }
}
